import React, { useState, useEffect } from 'react';
import { Star, Plus, RefreshCw, Trash2, Edit2, Database, Loader2 } from 'lucide-react';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import sql from 'react-syntax-highlighter/dist/esm/languages/hljs/sql';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { toast } from 'sonner';
import api from '../../services/api';
import { GoldenSQL, DatabaseConnection } from '../../types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

SyntaxHighlighter.registerLanguage('sql', sql);

const GoldenSQLs: React.FC = () => {
  const [goldenSQLs, setGoldenSQLs] = useState<GoldenSQL[]>([]);
  const [connections, setConnections] = useState<DatabaseConnection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingGoldenSQL, setEditingGoldenSQL] = useState<GoldenSQL | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    prompt_text: '',
    sql: '',
    schemas: '',
  });

  useEffect(() => {
    fetchConnections();
  }, []);

  useEffect(() => {
    if (selectedConnection) {
      fetchGoldenSQLs();
    }
  }, [selectedConnection]);

  const fetchConnections = async () => {
    try {
      const data = await api.getDatabaseConnections();
      setConnections(data);
      if (data.length > 0 && !selectedConnection) {
        setSelectedConnection(data[0].id || '');
      }
    } catch (error) {
      toast.error('Failed to fetch database connections');
    }
  };

  const fetchGoldenSQLs = async () => {
    if (!selectedConnection) return;
    
    setLoading(true);
    try {
      const data = await api.getGoldenSQLs({
        db_connection_id: selectedConnection,
        page: 1,
        limit: 100,
      });
      setGoldenSQLs(data);
    } catch (error) {
      toast.error('Failed to fetch golden SQLs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedConnection || !formData.prompt_text || !formData.sql) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const schemas = formData.schemas
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);

      if (editingGoldenSQL) {
        await api.updateGoldenSQL(editingGoldenSQL.id, {
          prompt_text: formData.prompt_text,
          sql: formData.sql,
          schemas: schemas.length > 0 ? schemas : undefined,
        });
        toast.success('Golden SQL updated successfully');
      } else {
        await api.createGoldenSQL({
          db_connection_id: selectedConnection,
          prompt_text: formData.prompt_text,
          sql: formData.sql,
          schemas: schemas.length > 0 ? schemas : undefined,
        });
        toast.success('Golden SQL created successfully');
      }
      
      setShowForm(false);
      setEditingGoldenSQL(null);
      setFormData({ prompt_text: '', sql: '', schemas: '' });
      fetchGoldenSQLs();
    } catch (error) {
      toast.error('Failed to save golden SQL');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this golden SQL?')) {
      return;
    }

    try {
      await api.deleteGoldenSQL(id);
      toast.success('Golden SQL deleted successfully');
      fetchGoldenSQLs();
    } catch (error) {
      toast.error('Failed to delete golden SQL');
    }
  };

  const handleEdit = (goldenSQL: GoldenSQL) => {
    setEditingGoldenSQL(goldenSQL);
    setFormData({
      prompt_text: goldenSQL.prompt_text,
      sql: goldenSQL.sql,
      schemas: goldenSQL.schemas?.join(', ') || '',
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingGoldenSQL(null);
    setFormData({ prompt_text: '', sql: '', schemas: '' });
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-6 w-6 text-yellow-500" />
                Golden SQLs
              </CardTitle>
              <CardDescription>
                Store verified SQL queries as examples to improve generation accuracy
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={selectedConnection} onValueChange={setSelectedConnection}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select a database..." />
                </SelectTrigger>
                <SelectContent>
                  {connections.map((conn) => (
                    <SelectItem key={conn.id} value={conn.id || ''}>
                      {conn.alias}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={fetchGoldenSQLs}
                disabled={loading || !selectedConnection}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                onClick={() => setShowForm(true)}
                disabled={!selectedConnection}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Golden SQL
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {!selectedConnection ? (
            <div className="text-center py-12">
              <Database className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Please select a database connection</p>
            </div>
          ) : loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : goldenSQLs.length === 0 ? (
            <div className="text-center py-12">
              <Star className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No golden SQLs found</p>
              <Button onClick={() => setShowForm(true)}>
                Add Your First Golden SQL
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {goldenSQLs.map((goldenSQL) => (
                <Card key={goldenSQL.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <p className="font-medium mb-1">{goldenSQL.prompt_text}</p>
                        {goldenSQL.schemas && goldenSQL.schemas.length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {goldenSQL.schemas.map((schema, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {schema}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(goldenSQL)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(goldenSQL.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="rounded overflow-hidden">
                      <SyntaxHighlighter
                        language="sql"
                        style={docco}
                        customStyle={{
                          padding: '0.5rem',
                          fontSize: '12px',
                          maxHeight: '150px',
                        }}
                      >
                        {goldenSQL.sql}
                      </SyntaxHighlighter>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Modal */}
      <Dialog open={showForm} onOpenChange={handleCancel}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingGoldenSQL ? 'Edit Golden SQL' : 'Add Golden SQL'}
            </DialogTitle>
            <DialogDescription>
              Store verified SQL queries to improve future generation accuracy
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="prompt">
                Natural Language Query <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="prompt"
                value={formData.prompt_text}
                onChange={(e) => setFormData({ ...formData, prompt_text: e.target.value })}
                rows={2}
                placeholder="e.g., Show me the top 10 customers by revenue"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sql">
                SQL Query <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="sql"
                value={formData.sql}
                onChange={(e) => setFormData({ ...formData, sql: e.target.value })}
                rows={6}
                className="font-mono text-sm"
                placeholder="SELECT customer_name, SUM(revenue) as total_revenue..."
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="schemas">Schemas (Optional)</Label>
              <Input
                id="schemas"
                type="text"
                value={formData.schemas}
                onChange={(e) => setFormData({ ...formData, schemas: e.target.value })}
                placeholder="public, analytics (comma-separated)"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingGoldenSQL ? 'Update' : 'Create'} Golden SQL
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GoldenSQLs;